// Role helpers for Battery Care.
//
// Hierarchy (least → most privileged):
//   viewer       — read-only, no writes
//   editor       — lot tech: can write check-ins
//   admin        — manager / used-car manager: full app within the store
//   storeAdmin   — store admin (isStoreAdmin claim): branding + light config
//   groupAdmin   — rooftop / group admin (isGroupAdmin claim): full control
//                  across every store in the tenant
//   platformAdmin— SEVRIC ourselves (isPlatformAdmin claim): controls which
//                  stores have access to which apps; handled in the portal.
//
// Important authorization model decision (2026-05-08):
//   Store admins can control CERTAIN features (display name, logo, ZIP).
//   Rooftop admins have FULL control (above + charge-cycle thresholds + lat/lng).
//   SEVRIC platform admins control app provisioning (NOT this app's settings page).
// See `canEditStoreSettings` + `canEditAdvancedStoreSettings` below.

export const ROLES = ["none", "viewer", "editor", "admin"];

export function roleOf(profile) {
  if (!profile) return "none";
  if (profile.isPlatformAdmin || profile.isGroupAdmin) return "admin";
  return profile.role || "none";
}

// Read access to inventory + manager view (anyone with a role above none).
export function canRead(profile) {
  return roleOf(profile) !== "none";
}

// Write access to check-in form (lot tech default).
export function canCheckIn(profile) {
  const r = roleOf(profile);
  return r === "editor" || r === "admin";
}

// Manager view + administrative actions (used car manager, store admin, group admin).
export function canManage(profile) {
  return roleOf(profile) === "admin" || profile?.isStoreAdmin;
}

// Open the Settings page at all. Store admins + rooftop admins + platform
// admins. NOT regular editors / techs. Inside the page the advanced helper
// below gates the rooftop-only fields.
export function canEditStoreSettings(profile) {
  if (!profile) return false;
  return !!(profile.isStoreAdmin || profile.isGroupAdmin || profile.isPlatformAdmin);
}

// Advanced (rooftop-and-above) settings. Charge-cycle threshold overrides,
// lat/lng direct entry, anything that affects operational behavior or
// crosses store boundaries within a tenant. Store admins can't touch these
// — they get a read-only display of the effective value with a tooltip
// explaining who can change it.
export function canEditAdvancedStoreSettings(profile) {
  if (!profile) return false;
  return !!(profile.isGroupAdmin || profile.isPlatformAdmin);
}
